'use client';

import { format, parseISO } from 'date-fns';
import flow from 'lodash/fp/flow';
import groupBy from 'lodash/fp/groupBy';
import sortBy from 'lodash/fp/sortBy';
import { useTranslations } from '@fshr/context/i18n-context';
import { Oj } from '@fshr/shared/types';

import { ShoppingCartItem } from '../index';
import { formatPrice, getNumberOfGuests } from '@fshr/shared';
import classNames from 'classnames';

interface ShoppingCartItineraryProps {
	itinerary: Oj.EventProduct[];
	removeItem: (params: { productId: string }) => void;
	insideCart?: boolean;
	handleShowInfoModal?: (hp: Oj.hotelProduct, priceStr: string) => void;
}

export default function ShoppingCartItinerary({
	itinerary,
	removeItem,
	insideCart = false,
	handleShowInfoModal,
}: ShoppingCartItineraryProps) {
	const t = useTranslations();

	const timeSlots = ['morning', 'lunch time', 'afternoon', 'dusk', 'evening'];

	const sortByTreatmentDate = (item: Oj.EventProduct) => item.date;

	const sortByTreatmentTime = (item: Oj.EventProduct) => {
		return timeSlots.indexOf(item.timeOfDay || '');
	};

	const groupedOrderedSpaEvents = flow(
		sortBy([sortByTreatmentDate, sortByTreatmentTime]),
		groupBy((item: Oj.EventProduct) => {
			return `${item.date}|${timeSlots.indexOf(item.timeOfDay?.toLowerCase() || '')}`;
		})
	)(itinerary);

	const eventKeys = Object.keys(groupedOrderedSpaEvents);

	return (
		<div className="flex flex-col">
			{eventKeys.map((key, index) => {
				const [date, timeSlotIndex] = key.split('|');
				const dateTimeToRender = [
					format(parseISO(date), 'MMM d'),
					timeSlots[parseInt(timeSlotIndex)],
				]
					.filter(Boolean)
					.join(', ');

				return (
					<div className="flex flex-row spacing-inline-xs">
						<div className="flex flex-col items-center self-stretch" aria-hidden={true}>
							{/* Dot */}
							<div className="relative flex h-2 w-2">
								<div className="absolute h-2 w-2 rounded-full bg-[rgb(77,77,77,0.45)]" />
							</div>
							{/* Line */}
							<div className="w-[1px] h-full bg-[rgb(77,77,77,0.45)]" />
						</div>
						<div
							key={key}
							className={classNames('flex flex-col spacing-stack-2xs w-full', {
								'pb-0': index === eventKeys.length - 1,
								'pb-100 md:pb-150 ': index < eventKeys.length - 1,
							})}
						>
							<span className="text-label">{dateTimeToRender}</span>
							<div className="flex flex-col spacing-stack-m">
								{groupedOrderedSpaEvents[key].map(
									({
										productId,
										name,
										productTotal,
										onRequestEventProductType,
										price,
										numAdults,
										numChildren,
										images,
										complimentaryPricingInfo,
									}) => (
										<ShoppingCartItem
											key={productId}
											name={name}
											duration={price?.priceName}
											numberOfPeople={getNumberOfGuests({
												t,
												numberOfAdults: numAdults || 0,
												numberOfChildren: numChildren || 0,
											})}
											price={productTotal?.totalPrice?.amount}
											currencyCode={productTotal?.totalPrice?.currencyCode}
											image={images?.[0]?.url}
											removeItem={() => removeItem({ productId: (productId || 0).toString() })}
											complimentaryPricingInfo={complimentaryPricingInfo}
											insideCart={insideCart}
											showInfoModal={() => {
												const hotelProduct = {
													productId,
													name,
													productTotal,
													price,
													numAdults,
													numChildren,
												} as Oj.hotelProduct;
												handleShowInfoModal?.(
													hotelProduct,
													formatPrice({
														value: productTotal?.totalPrice?.amount || 0,
														currencyCode: productTotal?.totalPrice?.currencyCode,
													})
												);
											}}
											onRequestEventProductType={onRequestEventProductType}
											isAncillary={true}
										/>
									)
								)}
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
}
